import React, { useEffect } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { useWaterStore } from '../store/useWaterStore';

export const WaterTimer: React.FC = () => {
  const { 
    reminderInterval, 
    isTimerActive, 
    timeRemaining,
    setReminderInterval, 
    setTimerActive,
    setTimeRemaining 
  } = useWaterStore();

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const playNotificationSound = () => {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      audio.play().catch(error => console.log('Audio playback failed:', error));
    };

    const showNotification = () => {
      if (!("Notification" in window)) {
        alert("This browser does not support desktop notifications");
        return;
      }

      if (Notification.permission === "granted") {
        playNotificationSound();
        new Notification("Time to drink water!", {
          body: "Stay hydrated! Take a sip of water.",
          icon: "https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=48&h=48&fit=crop"
        });
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            showNotification();
          }
        });
      }
    };

    if (isTimerActive) {
      timer = setInterval(() => {
        setTimeRemaining(Math.max(0, timeRemaining - 1));
        
        if (timeRemaining <= 1) {
          showNotification();
          setTimeRemaining(reminderInterval * 60);
        }
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [reminderInterval, isTimerActive, timeRemaining, setTimeRemaining]);


  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-center">Water Reminder</h2>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="reminder-interval-slider" className="block text-sm font-medium text-gray-700 mb-2">
            Reminder Interval (minutes)
          </label>
          <input
            id="reminder-interval-slider"
            type="range"
            min="5"
            max="120"
            step="5"
            value={reminderInterval}
            onChange={(e) => setReminderInterval(Number(e.target.value))}
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            aria-valuenow={reminderInterval}
            aria-valuemin={5}
            aria-valuemax={120}
            aria-label="Set reminder interval in minutes"
          />
          <div className="text-center mt-2 text-gray-600">
            {reminderInterval} minutes
          </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={async () => {
              if (!("Notification" in window)) {
                alert("This browser does not support desktop notifications");
                return;
              }
              
              if (Notification.permission === "default") {
                // If permission hasn't been asked yet, ask for it
                const permission = await Notification.requestPermission();
                if (permission === "granted") {
                  setTimerActive(!isTimerActive);
                }
              } else if (Notification.permission === "granted") {
                // If already granted, just toggle the timer
                setTimerActive(!isTimerActive);
              } else {
                // If denied, inform the user they need to enable notifications in browser settings
                alert("Notifications are blocked. Please enable them in your browser settings to receive water reminders.");
              }
            }}
            className={`flex items-center px-4 py-2 rounded-lg ${
              isTimerActive
                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
            }`}
            aria-label={isTimerActive ? "Stop water reminders" : "Start water reminders"}
          >
            {isTimerActive ? (
              <>
                <BellOff className="h-5 w-5 mr-2" aria-hidden="true" />
                Stop Reminders
              </>
            ) : (
              <>
                <Bell className="h-5 w-5 mr-2" aria-hidden="true" />
                Start Reminders
              </>
            )}
          </button>

          {isTimerActive && (
            <div className="text-2xl font-mono font-bold text-blue-600" role="timer" aria-label="Time remaining until next reminder">
              {formatTime(timeRemaining)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};